import React, { useState, useRef, useEffect } from 'react';
import './FollowUpHistoryWarning.css';

/**
 * FollowUpHistoryWarning — a small amber warning chip that sits next to the
 * Send button. The parent only mounts it when THIS follow-up step (or its
 * template) was already sent. A first follow-up after an intro stays quiet.
 */

const formatDate = (ms) => {
  if (!ms) return '';
  try {
    return new Date(ms).toLocaleString('en-US', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch (_) {
    return '';
  }
};

export default function FollowUpHistoryWarning({ sentCount = 0, lastSent = null }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!sentCount) return null;

  return (
    <div className="fuhw-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`fuhw-trigger ${open ? 'active' : ''}`}
        onClick={() => setOpen((v) => !v)}
        title="This follow-up step was already sent"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </button>

      {open && (
        <div className="fuhw-popover">
          <div className="fuhw-popover-head">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <span>This step was already sent</span>
          </div>
          <p className="fuhw-popover-text">
            This follow-up step was already sent {sentCount === 1 ? 'once' : `${sentCount} times`} to this address.
            Review it before you send the same step again.
          </p>
          {lastSent && (
            <div className="fuhw-popover-last">
              <span className="fuhw-popover-last-label">Last sent</span>
              <span className="fuhw-popover-last-subject">{lastSent.subject || '(no subject)'}</span>
              {lastSent.ms ? <span className="fuhw-popover-last-date">{formatDate(lastSent.ms)}</span> : null}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
